import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LogoMark from "../components/LogoMark";
import { api } from "../lib/api";
import { Shield, Users, Activity, Newspaper, Heart, DollarSign } from "lucide-react";

function ImpactStat({ icon, value, label, accent = "blue", testid }) {
  const color = accent === "green" ? "#39ff14" : "#00aaff";
  return (
    <div className="csc-card csc-card-hover p-5" style={{ borderColor: accent === "green" ? "rgba(57,255,20,0.25)" : "rgba(0,170,255,0.25)" }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}22` }}>
          {icon}
        </div>
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">{label}</div>
      </div>
      <div className="text-2xl lg:text-3xl font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

export function About() {
  const [form, setForm] = useState({ name:"", email:"", subject:"", message:"" });
  const [sent, setSent] = useState(false); const [err, setErr] = useState("");
  const [impact, setImpact] = useState(null);

  useEffect(() => {
    api.get(`/stats/impact`).then((r) => setImpact(r.data)).catch(() => {});
  }, []);

  const send = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post(`/contact`, form); setSent(true); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed."); }
  };

  const fmt = (n) => typeof n === "number" ? n.toLocaleString() : "—";
  const fmtMoney = (n) => typeof n === "number" ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "$0";

  return (
    <div className="min-h-screen bg-[#050d1a] text-white">
      <header className="flex items-center justify-between px-6 lg:px-12 py-5 border-b border-[rgba(0,170,255,0.18)]">
        <LogoMark size={36} withText /><Link to="/" className="csc-btn-ghost">← Back</Link>
      </header>
      <main className="max-w-5xl mx-auto px-6 lg:px-12 py-12" data-testid="about-page">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">About / Contact</div>
        <h1 className="text-3xl lg:text-5xl font-semibold mt-2 mb-2">Securing Today. <span style={{ color: "#39ff14" }}>Empowering Tomorrow.</span></h1>
        <p className="text-[#94a3b8] text-lg max-w-2xl">Free cybersecurity tools, trusted experts, and a global community — for the charities that protect people.</p>

        {/* Impact dashboard */}
        <section className="mt-10" data-testid="impact-dashboard">
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#00aaff] mb-3">// Our Impact (Live)</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <ImpactStat icon={<Users size={18} className="text-[#00aaff]" />} value={fmt(impact?.charities_supported)} label="Charities Supported" testid="impact-charities" />
            <ImpactStat icon={<Shield size={18} className="text-[#00aaff]" />} value={fmt(impact?.people_protected)} label="People Protected" testid="impact-people" />
            <ImpactStat icon={<Activity size={18} className="text-[#00aaff]" />} value={fmt(impact?.incidents_resolved)} label="Incidents Resolved" testid="impact-incidents" />
            <ImpactStat icon={<Newspaper size={18} className="text-[#00aaff]" />} value={fmt(impact?.intel_shared)} label="Intel Shared" testid="impact-intel" />
            <ImpactStat icon={<Heart size={18} className="text-[#39ff14]" />} value={fmt(impact?.donors)} label="Generous Donors" accent="green" testid="impact-donors" />
            <ImpactStat icon={<DollarSign size={18} className="text-[#39ff14]" />} value={fmtMoney(impact?.funds_raised_usd)} label="Funds Raised" accent="green" testid="impact-funds" />
          </div>
          <div className="font-mono-tech text-[10px] text-[#94a3b8] mt-3">Updated live from the platform · {impact?.as_of ? new Date(impact.as_of).toLocaleString() : "—"}</div>
        </section>

        <section className="csc-card p-8 mt-8 space-y-3 text-[#94a3b8] text-[15px] leading-relaxed">
          <p className="text-white text-base font-semibold">What we do</p>
          <p>CyberSecurity4Charities.org connects nonprofits with free cybersecurity tools, professional incident-response support, training, and a global community of practitioners. We exist because charities can't afford to choose between their mission and their security.</p>
          <p>If you run a nonprofit, register your organization and start using the tools today. If you're a security professional, volunteer your time. If you have means, donate — every dollar funds tools and incident response for an organization protecting people.</p>
        </section>

        <section className="csc-card p-8 mt-6" data-testid="contact-form-card">
          <h2 className="font-semibold mb-4">Contact Us</h2>
          {sent ? (
            <div className="text-[#39ff14]" data-testid="contact-sent">Thanks! Your message has been sent. We'll get back to you within 2 business days.</div>
          ) : (
            <form onSubmit={send} className="space-y-3">
              <input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Your name" className="csc-input" data-testid="contact-name" />
              <input required type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} placeholder="Email" className="csc-input" data-testid="contact-email" />
              <input required value={form.subject} onChange={(e)=>setForm({...form,subject:e.target.value})} placeholder="Subject" className="csc-input" data-testid="contact-subject" />
              <textarea required rows={5} value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} placeholder="Your message…" className="csc-input resize-none" data-testid="contact-message" />
              {err && <div className="text-xs text-red-400">{err}</div>}
              <button className="csc-btn-primary" data-testid="contact-send">Send</button>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}

export function NotFound() {
  return (
    <div className="min-h-screen bg-[#050d1a] flex flex-col items-center justify-center text-center px-4" data-testid="not-found-page">
      <LogoMark size={60} withText />
      <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14] mt-8">// 404</div>
      <h1 className="text-5xl font-bold mt-2">Page not found</h1>
      <p className="text-[#94a3b8] mt-2">The path you followed isn't on our threat map.</p>
      <Link to="/home" className="csc-btn-primary mt-6" data-testid="back-home-404">Back to safety →</Link>
    </div>
  );
}
